package com.arena_web.data.dto

import kotlinx.serialization.Serializable

// Arena News (/v1/news) + football data proxy (/v1/football/*) shapes —
// mirror Arena_Backend's NewsArticleDto / FootballDto. Admin-queue shapes
// live in the dedicated Pro-play-arena-admin-web project.

@Serializable
data class NewsArticleDto(
    val id: String,
    val title: String,
    val summary: String = "",
    val body: String = "",
    val coverImage: String = "",
    val sport: String = "football",
    /** NEWS | SPECIAL. */
    val type: String = "NEWS",
    val sourceName: String = "",
    val sourceUrl: String = "",
    val locale: String = "en",
    /** Lowercase tag slugs; tapping one filters the feed (?tag=). */
    val hashtags: List<String> = emptyList(),
    val viewsCount: Int = 0,
    val publishedAt: String? = null,
    val match: NewsMatchLinkDto? = null,
)

/** Linked-match snapshot on an article — card data + resolve context for
 *  /news/match/{id} (mirrors Arena_Backend's NewsMatchLinkDto). */
@Serializable
data class NewsMatchLinkDto(
    val id: String,
    val sport: String = "football",
    /** ISO-8601 UTC kickoff. */
    val utcDate: String = "",
    val competitionCode: String = "",
    val competitionName: String = "",
    val homeTeam: String = "",
    val awayTeam: String = "",
    val homeCrest: String = "",
    val awayCrest: String = "",
)

@Serializable
data class NewsPageDto(
    val items: List<NewsArticleDto>,
    val page: Int,
    val size: Int,
    val totalItems: Long,
    val totalPages: Int,
)

@Serializable
data class FootballCompetitionDto(
    val code: String,
    val name: String,
    val emblem: String = "",
)

@Serializable
data class FootballMatchDto(
    val id: String,
    val competitionCode: String = "",
    val competitionName: String = "",
    /** ISO-8601 UTC kickoff. */
    val utcDate: String = "",
    /** SCHEDULED | TIMED | IN_PLAY | PAUSED | FINISHED | POSTPONED | SUSPENDED | CANCELLED. */
    val status: String = "SCHEDULED",
    val homeTeam: String = "",
    val homeCrest: String = "",
    val awayTeam: String = "",
    val awayCrest: String = "",
    val homeScore: Int? = null,
    val awayScore: Int? = null,
)

@Serializable
data class LineupEntryDto(
    val team: String = "",
    val player: String = "",
    val playerId: String? = null,
    val position: String = "",
    val substitute: Boolean = false,
    val photo: String? = null,
    val number: String? = null,
    val home: Boolean = false,
)

@Serializable
data class TimelineEntryDto(
    val minute: Int? = null,
    val type: String = "",
    val detail: String? = null,
    val player: String = "",
    val playerId: String? = null,
    val assist: String? = null,
    val photo: String? = null,
    val team: String = "",
    val home: Boolean = false,
)

@Serializable
data class StatEntryDto(
    val name: String = "",
    val home: String = "",
    val away: String = "",
)

@Serializable
data class MatchDetailDto(
    val eventId: String = "",
    val venue: String? = null,
    val status: String = "SCHEDULED",
    val utcDate: String = "",
    val thumb: String? = null,
    val banner: String? = null,
    val video: String? = null,
    val homeTeam: String = "",
    val awayTeam: String = "",
    val homeTeamId: String? = null,
    val awayTeamId: String? = null,
    val homeCrest: String? = null,
    val awayCrest: String? = null,
    val homeScore: Int? = null,
    val awayScore: Int? = null,
    val lineup: List<LineupEntryDto> = emptyList(),
    val timeline: List<TimelineEntryDto> = emptyList(),
    val stats: List<StatEntryDto> = emptyList(),
    val homeRecent: List<FootballMatchDto> = emptyList(),
    val awayRecent: List<FootballMatchDto> = emptyList(),
)

@Serializable
data class SquadPlayerDto(
    val id: String? = null,
    val name: String = "",
    val position: String? = null,
    val photo: String? = null,
)

@Serializable
data class PlayerDetailDto(
    val id: String = "",
    val name: String = "",
    val team: String? = null,
    val position: String? = null,
    val nationality: String? = null,
    val born: String? = null,
    val birthLocation: String? = null,
    val height: String? = null,
    val weight: String? = null,
    val number: String? = null,
    val photo: String? = null,
    val description: String? = null,
)

@Serializable
data class HeadToHeadDto(
    val homeName: String = "",
    val awayName: String = "",
    val homeWins: Int = 0,
    val draws: Int = 0,
    val awayWins: Int = 0,
    val matches: List<FootballMatchDto> = emptyList(),
)

@Serializable
data class TeamDetailDto(
    val id: String = "",
    val name: String = "",
    val badge: String? = null,
    val banner: String? = null,
    val stadium: String? = null,
    val location: String? = null,
    val formedYear: String? = null,
    val description: String? = null,
    val squad: List<SquadPlayerDto> = emptyList(),
    val lastMatches: List<FootballMatchDto> = emptyList(),
    val nextMatches: List<FootballMatchDto> = emptyList(),
)

@Serializable
data class FootballStandingRowDto(
    val position: Int,
    val team: String,
    val crest: String = "",
    val played: Int = 0,
    val won: Int = 0,
    val draw: Int = 0,
    val lost: Int = 0,
    val goalsFor: Int = 0,
    val goalsAgainst: Int = 0,
    val goalDifference: Int = 0,
    val points: Int = 0,
    /** Last matches as "W,L,D,..." (null when the provider omits it). */
    val form: String? = null,
)

@Serializable
data class FootballStandingsDto(
    val competition: FootballCompetitionDto,
    val rows: List<FootballStandingRowDto>,
)
